import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService, OrganizerEmailMode } from '../../services/api.service';
import { useAutoSave } from '../../hooks/useAutoSave';
import AutoSaveIndicator from '../ui/AutoSaveIndicator';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';

const MODES: OrganizerEmailMode[] = ['immediate', 'daily', 'none'];

/**
 * Comment l'organisateur apprend une inscription ou un bénévole (2026-09-27).
 *
 * Réglage privé : il ne rend rien public et n'écrit chez personne, donc il se
 * sauve au clic comme le reste de l'écran. Il a sa propre route (et son propre
 * indicateur) parce qu'il vit sur l'événement, pas dans les paramètres
 * d'inscription.
 */
const OrganizerEmailModeField: React.FC<{ eventId: string }> = ({ eventId }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<OrganizerEmailMode | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const auto = useAutoSave<{ organizer_email_mode: OrganizerEmailMode }>(
    useCallback(async (patch) => {
      await apiService.setOrganizerEmailMode(eventId, patch.organizer_email_mode);
    }, [eventId]),
    { onError: (e: any) => setErreur(e?.response?.data?.detail || t('common.error')) },
  );

  useEffect(() => {
    let actif = true;
    setMode(null);
    apiService.getOrganizerEmailMode(eventId)
      .then((r) => { if (actif) setMode(r.data.organizer_email_mode); })
      .catch((e: any) => { if (actif) setErreur(e?.response?.data?.detail || t('common.error')); });
    return () => { actif = false; };
  }, [eventId, t]);

  const choisir = (valeur: string) => {
    const precedent = mode;
    const nouveau = valeur as OrganizerEmailMode;
    setMode(nouveau);
    setErreur(null);
    void auto.commit({ organizer_email_mode: nouveau }, () => setMode(precedent));
  };

  return (
    <div className="space-y-3 pt-4 border-t border-border">
      <div className="flex items-center justify-between gap-3">
        <Label>{t('organizerEmails.title')}</Label>
        <AutoSaveIndicator status={auto.status} retry={auto.retry} />
      </div>
      <p className="text-xs text-muted-foreground">{t('organizerEmails.help')}</p>
      {mode && (
        <RadioGroup value={mode} onValueChange={choisir} className="gap-3">
          {MODES.map((m) => (
            <div key={m} className="flex items-start gap-2">
              <RadioGroupItem id={`organizer-email-${m}`} value={m} className="mt-0.5 border-white/55 text-white" />
              <Label htmlFor={`organizer-email-${m}`} className="font-normal leading-snug cursor-pointer">
                {t(`organizerEmails.${m}`)}
                {m === 'daily' && (
                  <span className="block text-xs text-muted-foreground">{t('organizerEmails.daily_hint')}</span>
                )}
              </Label>
            </div>
          ))}
        </RadioGroup>
      )}
      {erreur && <p className="text-xs text-destructive">{erreur}</p>}
    </div>
  );
};

export default OrganizerEmailModeField;
